/**
 * RequestDeliverablesDialog — the deliverables confirmation at Submit (FR20/FR21).
 *
 * On a Draft this **replaces** the "Confirm Stage Change" dialog rather than following it:
 * one dialog at submit, and two modals in sequence read as a defect. Its confirm button is
 * the confirmation, so it carries the submit button's own label.
 *
 * Since cycle 4b it is **controlled** and writes nothing. Deliverables are request fields
 * now, held pending until a save commits them, and the save this dialog belongs to is the
 * one Submit already performs. So the dialog reports add / edit / remove to the screen, the
 * screen holds them, and confirming the submit is what persists them.
 *
 * The intended consequence, which is in the I/O matrix: **adding a deliverable inside the
 * dialog and then pressing Cancel discards it**, along with the submit.
 *
 * A react-native `Modal` rather than `ConfirmationDialog`, which is title + message +
 * buttons only and has no `children` prop. Shell follows `ImportPreCreationModal`, which
 * likewise blocks its action until validation passes.
 */

import React, { useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight } from '../../theme/typography';
import { RequestDeliverablesEditor } from '../deliverables/RequestDeliverablesEditor';
import { X } from '../icons';

import type {
  RequestDeliverable,
  RequestDeliverableEntry,
  RequestDeliverableType,
} from '../../types/requestDeliverables';

export interface RequestDeliverablesDialogProps {
  visible: boolean;
  /** The request's deliverables, pending changes included. Owned by the screen. */
  deliverables: RequestDeliverable[];
  onAdd: (entries: RequestDeliverableEntry[]) => void;
  onEdit: (itemType: RequestDeliverableType, comment: string) => void;
  onRemove: (itemType: RequestDeliverableType) => void;
  /** Cancels the submit **and** the deliverable changes made in here. */
  onCancel: () => void;
  /** Confirms the submit; the save it performs is what persists the deliverables. */
  onConfirm: () => void;
  /** Matches the submit button ("Submit" / "Send to Vertiv"). */
  confirmLabel: string;
  /**
   * The third exit: commit what is pending and close, without touching the stage.
   *
   * Reported rather than performed, like `onAdd` / `onEdit` / `onRemove` — the screen owns the
   * write. Deliberately NOT routed through `handleConfirm`: that enforces "at least one
   * deliverable", which is a rule about submitting. Saving an empty list is a legitimate write
   * that clears the request.
   */
  onSaveChanges?: () => void;
  /**
   * Whether there is anything a save would write. The control is absent, not disabled, when
   * there is not: an inert button on a dialog whose whole job is submitting is noise.
   */
  canSaveChanges?: boolean;
}

const VALIDATION_MESSAGE = 'Add at least one deliverable before submitting.';

export const RequestDeliverablesDialog: React.FC<
  RequestDeliverablesDialogProps
> = ({
  visible,
  deliverables,
  onAdd,
  onEdit,
  onRemove,
  onCancel,
  onConfirm,
  confirmLabel,
  onSaveChanges,
  canSaveChanges = false,
}) => {
  const [error, setError] = useState<string | null>(null);

  // Cleared on every open: a validation message left over from a previous submit attempt
  // would greet the user before they had done anything.
  useEffect(() => {
    if (visible) setError(null);
  }, [visible]);

  /**
   * Cleared again the moment the rejection stops being true.
   *
   * The only rejection is "nothing stated", and ticking a box now states something — so
   * without this the message sits under a visibly valid form describing a state the user has
   * already fixed, which reads as a live block on submitting. It could not arise before the
   * checklist: a tick used to leave `deliverables` untouched, so the message stayed accurate
   * until "Add all checked" was pressed.
   */
  useEffect(() => {
    if (deliverables.length > 0) setError(null);
  }, [deliverables.length]);

  const handleConfirm = () => {
    // FR21a: at least one deliverable must exist, counting what is pending in this session.
    // A ticked box now IS a pending deliverable, so this count sees everything on screen —
    // the earlier "you ticked something but did not press Add all checked" wording described
    // a state that no longer exists.
    if (deliverables.length === 0) {
      setError(VALIDATION_MESSAGE);
      return;
    }
    onConfirm();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.overlay} testID="request-deliverables-dialog">
        <View style={styles.card}>
          <View style={styles.header}>
            <View style={styles.headerText}>
              <Text style={styles.title}>
                What should this request deliver?
              </Text>
              <Text style={styles.subtitle}>
                Tell us what you need Vertiv to produce. You can change this
                later.
              </Text>
            </View>
            <TouchableOpacity
              onPress={onCancel}
              style={styles.closeButton}
              testID="request-deliverables-dialog-close"
              accessibilityLabel="Close"
            >
              <X size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.body}>
            {visible ? (
              <RequestDeliverablesEditor
                deliverables={deliverables}
                onAdd={onAdd}
                onEdit={onEdit}
                onRemove={onRemove}
              />
            ) : null}
          </ScrollView>

          {!!error && (
            <Text
              style={styles.errorText}
              testID="request-deliverables-dialog-error"
            >
              {error}
            </Text>
          )}

          <View style={styles.footer}>
            {/*
              The left slot is ALWAYS rendered, empty when there is nothing to save. With
              `space-between` and a single child the Cancel/Submit group would slide to the
              left edge and back again as the user ticked, moving Submit under their cursor.
              Holding the slot keeps the geometry constant and changes only its contents.
            */}
            <View
              style={styles.footerLeft}
              testID="request-deliverables-dialog-footer-left"
            >
              {canSaveChanges && !!onSaveChanges && (
                <TouchableOpacity
                  onPress={onSaveChanges}
                  style={styles.saveChangesButton}
                  testID="request-deliverables-dialog-save"
                  accessibilityRole="button"
                  accessibilityLabel="Save changes without submitting"
                >
                  <Text style={styles.saveChangesText} numberOfLines={1}>
                    Save changes
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.footerActions}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={onCancel}
                testID="request-deliverables-dialog-cancel"
              >
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.button, styles.confirmButton]}
                onPress={handleConfirm}
                testID="request-deliverables-dialog-confirm"
              >
                <Text style={styles.confirmText}>{confirmLabel}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: colors.overlayBackdrop,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.screenPaddingHorizontal,
  },
  card: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.formControlBorderRadius,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 520 : 460,
    maxHeight: '86%',
    paddingVertical: spacing.screenPaddingVertical,
    paddingHorizontal: spacing.formSectionContentPaddingWeb,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerText: { flex: 1 },
  title: {
    fontSize: fontSize.sectionTitle,
    lineHeight: lineHeight.sectionTitle,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  subtitle: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
    marginTop: spacing.charCountMarginTop,
  },
  closeButton: { padding: spacing.infoIconPadding },
  body: { flexGrow: 0 },
  errorText: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.error,
    marginTop: spacing.formInlineFeedbackGap,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    // The save exit sits at the far left, away from the pair that ends the submit, so the
    // three actions cannot be mistaken for one row of equals.
    justifyContent: 'space-between',
    gap: spacing.formControlIconGap,
    marginTop: spacing.fieldRowMarginBottom,
  },
  /** Holds the left slot whether or not there is anything in it. See the render comment. */
  footerLeft: {
    flexShrink: 1,
  },
  footerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.formControlIconGap,
    flexShrink: 0,
  },
  /**
   * Text style, no border and no fill: third in the hierarchy behind the filled Submit and the
   * outlined Cancel. It is an escape hatch, not a competing call to action.
   *
   * `minHeight` because padding alone left the tap target around 37pt, under the 44pt iOS
   * minimum (adversarial review, 2026-08-12). Padding on both sides for the same reason: with
   * `paddingRight` only, the target started flush against the card's content edge.
   */
  saveChangesButton: {
    justifyContent: 'center',
    minHeight: spacing.minimumTapTarget,
    paddingHorizontal: spacing.formCheckboxLabelGap,
    flexShrink: 1,
  },
  saveChangesText: {
    color: colors.textSecondary,
    fontSize: fontSize.formLabel,
    fontWeight: '600',
  },
  button: {
    paddingVertical: spacing.formCompactActionPaddingVertical,
    paddingHorizontal: spacing.formCompactActionPaddingHorizontal,
    borderRadius: spacing.formControlBorderRadius,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
  },
  cancelText: {
    color: colors.textPrimary,
    fontSize: fontSize.formLabel,
    fontWeight: '600',
  },
  confirmButton: { backgroundColor: colors.primary },
  confirmText: {
    color: colors.textLight,
    fontSize: fontSize.formLabel,
    fontWeight: '700',
  },
});
